import React, { useState, useEffect, useMemo } from 'react';
import { ChangeLogEntry, ChangeLogAction } from '../types';
import { getChangeLogByGoal } from '../firebase/changelog.service';
import { GoalIcon, EditIcon, DocumentIcon, CalendarIcon, MilestoneIcon, AddIcon, SuccessIcon, DeleteIcon, GrowthIcon, AnalyticsIcon, CheckIcon } from '../../../icons';
import { RefreshCw } from 'lucide-react';

interface ChangeLogOverviewProps {
  goalId: string;
  ownerId: string;
}

// Action icons and labels
const ACTION_CONFIG: Record<ChangeLogAction, { icon: React.ReactNode; label: string; color: string }> = {
  // Goal actions
  goal_created: { icon: <GoalIcon size={16} className="text-green-600" />, label: 'Reis gestart', color: 'text-green-600' },
  goal_title_changed: { icon: <EditIcon size={16} className="text-blue-600" />, label: 'Doeltitel gewijzigd', color: 'text-blue-600' },
  goal_description_changed: { icon: <DocumentIcon size={16} className="text-blue-600" />, label: 'Beschrijving aangepast', color: 'text-blue-600' },
  goal_start_date_changed: { icon: <CalendarIcon size={16} className="text-orange-600" />, label: 'Startdatum verschoven', color: 'text-orange-600' },
  goal_end_date_changed: { icon: <CalendarIcon size={16} className="text-orange-600" />, label: 'Einddatum verschoven', color: 'text-orange-600' },
  goal_completed: { icon: <MilestoneIcon size={16} className="text-green-600" />, label: 'Doel behaald', color: 'text-green-600' },
  // Milestone actions
  milestone_created: { icon: <AddIcon size={16} className="text-green-600" />, label: 'Mijlpaal toegevoegd', color: 'text-green-600' },
  milestone_title_changed: { icon: <EditIcon size={16} className="text-blue-600" />, label: 'Mijlpaal titel gewijzigd', color: 'text-blue-600' },
  milestone_date_changed: { icon: <CalendarIcon size={16} className="text-orange-600" />, label: 'Mijlpaal datum verschoven', color: 'text-orange-600' },
  milestone_completed: { icon: <SuccessIcon size={16} className="text-green-600" />, label: 'Mijlpaal afgerond', color: 'text-green-600' },
  milestone_deleted: { icon: <DeleteIcon size={16} className="text-red-600" />, label: 'Mijlpaal verwijderd', color: 'text-red-600' },
  // Video actions
  video_recorded: { icon: <GoalIcon size={16} className="text-purple-600" />, label: 'Video opgenomen', color: 'text-purple-600' },
  video_rerecorded: { icon: <RefreshCw size={16} className="text-purple-600" />, label: 'Video opnieuw opgenomen', color: 'text-purple-600' },
  video_deleted: { icon: <DeleteIcon size={16} className="text-red-600" />, label: 'Video verwijderd', color: 'text-red-600' },
  // Journey actions
  journey_started: { icon: <GrowthIcon size={16} className="text-green-600" />, label: 'Reis gestart', color: 'text-green-600' },
  journey_completed: { icon: <MilestoneIcon size={16} className="text-green-600" />, label: 'Reis afgerond', color: 'text-green-600' },
};

/**
 * Format date for display
 */
function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format time for display
 */
function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format date difference for display
 */
function formatDateDiff(prev: string, next: string): string {
  const prevDate = new Date(prev);
  const nextDate = new Date(next);
  const diffDays = Math.round((nextDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return '';
  if (diffDays > 0) return `(+${diffDays} dagen)`;
  return `(${diffDays} dagen)`;
}

/**
 * ChangeLogOverview - Wijzigingslog
 * 
 * Shows a chronological history of all changes to goals, milestones, and videos.
 * Replaces the old TimeContextOverview.
 */
const ChangeLogOverview: React.FC<ChangeLogOverviewProps> = ({
  goalId,
  ownerId,
}) => {
  const [entries, setEntries] = useState<ChangeLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load changelog entries
  useEffect(() => {
    const loadEntries = async () => {
      setIsLoading(true);
      try {
        const data = await getChangeLogByGoal(ownerId, goalId);
        setEntries(data);
      } catch (err) {
        console.error('Failed to load changelog:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadEntries();
  }, [goalId, ownerId]);

  // Group entries by date
  const entriesByDate = useMemo(() => {
    const grouped: Record<string, ChangeLogEntry[]> = {};
    entries.forEach(entry => {
      const dateKey = formatDate(entry.createdAt);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(entry);
    });
    return grouped;
  }, [entries]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const videoRerecords = entries.filter(e => e.action === 'video_rerecorded').length;
    const dateChanges = entries.filter(e => 
      e.action === 'milestone_date_changed' || 
      e.action === 'goal_start_date_changed' || 
      e.action === 'goal_end_date_changed'
    ).length;
    const milestonesDeleted = entries.filter(e => e.action === 'milestone_deleted').length;
    const milestonesCompleted = entries.filter(e => e.action === 'milestone_completed').length;
    
    return { videoRerecords, dateChanges, milestonesDeleted, milestonesCompleted };
  }, [entries]);

  if (isLoading) {
    return (
      <div className="text-center py-6 text-gray-400 text-sm">
        Laden...
      </div>
    );
  }

  // Empty state
  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <span className="mb-2 block"><DocumentIcon size={32} className="text-gray-400 mx-auto" /></span>
        <p className="text-sm">Nog geen wijzigingen gelogd</p>
        <p className="text-xs mt-1">Wijzigingen aan mijlpalen en video's verschijnen hier</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      {(stats.videoRerecords > 0 || stats.dateChanges > 0 || stats.milestonesDeleted > 0) && (
        <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
          <div className="font-medium mb-1 flex items-center gap-1"><AnalyticsIcon size={14} className="text-gray-500" /> Samenvatting</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {stats.milestonesCompleted > 0 && (
              <span className="flex items-center gap-1"><SuccessIcon size={12} className="text-green-500" /> {stats.milestonesCompleted}x mijlpaal afgerond</span>
            )}
            {stats.videoRerecords > 0 && (
              <span className="flex items-center gap-1"><RefreshCw size={12} className="text-purple-500" /> {stats.videoRerecords}x video opnieuw opgenomen</span>
            )}
            {stats.dateChanges > 0 && (
              <span className="flex items-center gap-1"><CalendarIcon size={12} className="text-orange-500" /> {stats.dateChanges}x datum aangepast</span>
            )}
            {stats.milestonesDeleted > 0 && (
              <span className="flex items-center gap-1"><DeleteIcon size={12} className="text-red-500" /> {stats.milestonesDeleted}x mijlpaal verwijderd</span>
            )}
          </div>
        </div>
      )}

      {/* Entries grouped by date */}
      <div className="space-y-4">
        {Object.entries(entriesByDate).map(([date, dateEntries]) => (
          <div key={date}>
            {/* Date header */}
            <div className="text-xs font-medium text-gray-500 mb-2 sticky top-0 bg-white py-1">
              {date}
            </div>
            
            {/* Entries for this date */}
            <div className="space-y-2 ml-2 border-l-2 border-gray-100 pl-4">
              {dateEntries.map(entry => {
                const config = ACTION_CONFIG[entry.action];
                
                return (
                  <div key={entry.id} className="relative">
                    {/* Timeline dot */}
                    <div className="absolute -left-[21px] top-1 w-2 h-2 rounded-full bg-gray-300" />
                    
                    {/* Entry content */}
                    <div className="text-sm">
                      {/* Time and action */}
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-xs w-12">
                          {formatTime(entry.createdAt)}
                        </span>
                        <span>{config.icon}</span>
                        <span className={config.color}>{config.label}</span>
                      </div>
                      
                      {/* Entity title */}
                      {entry.entityTitle && (
                        <div className="ml-14 text-gray-600 text-xs">
                          "{entry.entityTitle}"
                        </div>
                      )}
                      
                      {/* Value change (for date/title changes) */}
                      {entry.previousValue && entry.newValue && (
                        <div className="ml-14 text-gray-500 text-xs">
                          <span className="line-through">{entry.previousValue}</span>
                          <span className="mx-1">→</span>
                          <span className="font-medium">{entry.newValue}</span>
                          {(entry.action === 'milestone_date_changed' || 
                            entry.action === 'goal_start_date_changed' || 
                            entry.action === 'goal_end_date_changed') && (
                            <span className="text-orange-500 ml-1">
                              {formatDateDiff(entry.previousValue, entry.newValue)}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Re-record count */}
                      {entry.action === 'video_rerecorded' && entry.metadata?.rerecordCount && (
                        <div className="ml-14 text-gray-500 text-xs">
                          ({entry.metadata.rerecordCount}e keer)
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChangeLogOverview;
